/**
 * Enterprise SaaS Security Module Component
 * SaaS Application Compliance Monitoring
 */

import React, { useState, useEffect } from 'react';
import { useI18n } from '../../context/I18nContext';
import { saasService } from '../../services/enterpriseService';
import { grcService } from '../../services/grcService';
import { Modal, ControlSelector, useEvidenceIntegration, performSaaSComplianceCheck } from './SharedModals';
import {
  Layers, CheckCircle, XCircle, AlertTriangle, RefreshCw, Plus,
  Settings, Search, Filter, ExternalLink, Shield, Lock, Unlock,
  Users, Database, Eye, Clock, Activity, TrendingUp, LinkIcon, Play
} from 'lucide-react';

interface SaaSIntegration {
  id: number;
  app_name: string;
  vendor_name: string;
  category: string;
  connection_status: 'connected' | 'disconnected' | 'error';
  compliance_status: 'compliant' | 'non_compliant' | 'partial' | 'unknown';
  last_sync_at?: string;
  user_count?: number;
  data_classification: 'public' | 'internal' | 'confidential' | 'restricted';
  sso_enabled: boolean;
  mfa_enforced: boolean;
}

interface ComplianceSummary {
  total_apps: number;
  compliant: number;
  non_compliant: number;
  partial: number;
  sso_adoption: number;
  mfa_adoption: number;
}

export const EnterpriseSaaS: React.FC<{ frameworkName?: string }> = ({ frameworkName }) => {
  const { language } = useI18n();
  const [integrations, setIntegrations] = useState<SaaSIntegration[]>([]);
  const [summary, setSummary] = useState<ComplianceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'apps' | 'compliance'>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');

  // Modal states
  const [showAddAppModal, setShowAddAppModal] = useState(false);
  const [showComplianceCheckModal, setShowComplianceCheckModal] = useState(false);
  const [showLinkControlsModal, setShowLinkControlsModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState<SaaSIntegration | null>(null);
  const [saving, setSaving] = useState(false);
  const [checkingCompliance, setCheckingCompliance] = useState(false);
  const [complianceResults, setComplianceResults] = useState<any>(null);

  // New app form
  const [newApp, setNewApp] = useState({
    app_name: '',
    vendor_name: '',
    category: 'productivity',
    data_classification: 'internal' as SaaSIntegration['data_classification']
  });

  // Control linking
  const [frameworks, setFrameworks] = useState<any[]>([]);
  const [selectedFramework, setSelectedFramework] = useState<number | null>(null);
  const [selectedControls, setSelectedControls] = useState<number[]>([]);
  const { addEvidence } = useEvidenceIntegration();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [integrationsData, summaryData] = await Promise.all([
        saasService.getIntegrations(),
        saasService.getComplianceSummary().catch(() => null)
      ]);
      
      // Handle API response format
      const integrationsList = integrationsData?.data || integrationsData?.integrations || (Array.isArray(integrationsData) ? integrationsData : []);
      setIntegrations(integrationsList);
      setSummary(summaryData);
    } catch (error) {
      console.error('Error loading SaaS data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (integrationId: number) => {
    try {
      await saasService.syncIntegration(integrationId);
      loadData();
    } catch (error) {
      console.error('Error syncing:', error);
    }
  };

  const loadFrameworks = async () => {
    try {
      const data = await grcService.getFrameworks();
      const frameworkList = Array.isArray(data) ? data : ((data as any)?.frameworks || []);
      setFrameworks(frameworkList);
    } catch (error) {
      console.error('Error loading frameworks:', error);
    }
  };

  const handleAddApp = async () => {
    if (!newApp.app_name || !newApp.vendor_name) return;
    setSaving(true);
    try {
      await saasService.createIntegration(newApp);
      setShowAddAppModal(false);
      setNewApp({ app_name: '', vendor_name: '', category: 'productivity', data_classification: 'internal' });
      loadData();
    } catch (error) {
      console.error('Error adding app:', error);
    } finally {
      setSaving(false);
    }
  };

  const runComplianceCheck = async (app: SaaSIntegration) => {
    setSelectedApp(app);
    setCheckingCompliance(true);
    setShowComplianceCheckModal(true);
    setComplianceResults(null);
    try {
      const results = await performSaaSComplianceCheck(app.id.toString());
      setComplianceResults(results);
    } catch (error) {
      console.error('Error checking compliance:', error);
      setComplianceResults({ error: true });
    } finally {
      setCheckingCompliance(false);
    }
  };

  const openLinkModal = (app: SaaSIntegration) => {
    setSelectedApp(app);
    setSelectedControls([]);
    setSelectedFramework(null);
    loadFrameworks();
    setShowLinkControlsModal(true);
  };

  const handleLinkToControls = async () => {
    if (!selectedApp || selectedControls.length === 0) return;
    setSaving(true);
    try {
      for (const controlId of selectedControls) {
        await addEvidence(
          controlId,
          `SaaS Compliance: ${selectedApp.app_name}`,
          `Compliance status: ${selectedApp.compliance_status}, SSO: ${selectedApp.sso_enabled ? 'Yes' : 'No'}, MFA: ${selectedApp.mfa_enforced ? 'Yes' : 'No'}`,
          'SaaS Module'
        );
      }
      setShowLinkControlsModal(false);
      setSelectedApp(null);
    } catch (error) {
      console.error('Error linking to controls:', error);
    } finally {
      setSaving(false);
    }
  };

  const categories = [
    { key: 'all', label: language === 'sr' ? 'Sve' : 'All' },
    { key: 'productivity', label: language === 'sr' ? 'Produktivnost' : 'Productivity' },
    { key: 'security', label: language === 'sr' ? 'Sigurnost' : 'Security' },
    { key: 'crm', label: 'CRM' },
    { key: 'hr', label: 'HR' },
    { key: 'finance', label: language === 'sr' ? 'Financije' : 'Finance' },
    { key: 'other', label: language === 'sr' ? 'Ostalo' : 'Other' }
  ];

  const complianceColors = {
    compliant: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    non_compliant: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    partial: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    unknown: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
  };

  const dataClassificationColors = {
    public: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    internal: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    confidential: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    restricted: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
  };

  const filteredIntegrations = integrations.filter(integration => {
    const matchesSearch = integration.app_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      integration.vendor_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || integration.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <Layers className="w-7 h-7 text-cyan-500" />
            {language === 'sr' ? 'SaaS sigurnost' : 'SaaS Security'}
            {frameworkName && (
              <span className="px-3 py-1 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 rounded-full text-sm font-medium">
                {frameworkName}
              </span>
            )}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {language === 'sr' 
              ? 'Pratite usklađenost SaaS aplikacija'
              : 'Monitor SaaS application compliance'
            }
          </p>
        </div>
        <button 
          onClick={() => setShowAddAppModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {language === 'sr' ? 'Dodaj aplikaciju' : 'Add Application'}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <Layers className="w-8 h-8 text-cyan-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">{integrations.length}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'SaaS aplikacija' : 'SaaS Apps'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {integrations.filter(i => i.compliance_status === 'compliant').length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Usklađeno' : 'Compliant'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {integrations.filter(i => i.compliance_status === 'non_compliant').length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'Neusklađeno' : 'Non-Compliant'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <Lock className="w-8 h-8 text-indigo-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {integrations.filter(i => i.sso_enabled).length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'SSO omogućen' : 'SSO Enabled'}</div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center justify-between mb-3">
            <Shield className="w-8 h-8 text-purple-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {integrations.filter(i => i.mfa_enforced).length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{language === 'sr' ? 'MFA prisilan' : 'MFA Enforced'}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        {[
          { key: 'overview', icon: TrendingUp, label: language === 'sr' ? 'Pregled' : 'Overview' },
          { key: 'apps', icon: Layers, label: language === 'sr' ? 'Aplikacije' : 'Applications' },
          { key: 'compliance', icon: Shield, label: language === 'sr' ? 'Usklađenost' : 'Compliance' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as typeof activeTab)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Security Posture */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {language === 'sr' ? 'Sigurnosna pozicija' : 'Security Posture'}
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">SSO {language === 'sr' ? 'usvajanje' : 'Adoption'}</span>
                  <span className="text-sm font-semibold">{Math.round((integrations.filter(i => i.sso_enabled).length / integrations.length) * 100) || 0}%</span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                  <div 
                    className="h-2 bg-indigo-500 rounded-full" 
                    style={{ width: `${(integrations.filter(i => i.sso_enabled).length / integrations.length) * 100 || 0}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">MFA {language === 'sr' ? 'usvajanje' : 'Adoption'}</span>
                  <span className="text-sm font-semibold">{Math.round((integrations.filter(i => i.mfa_enforced).length / integrations.length) * 100) || 0}%</span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                  <div 
                    className="h-2 bg-purple-500 rounded-full" 
                    style={{ width: `${(integrations.filter(i => i.mfa_enforced).length / integrations.length) * 100 || 0}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{language === 'sr' ? 'Usklađenost' : 'Compliance'}</span>
                  <span className="text-sm font-semibold">{Math.round((integrations.filter(i => i.compliance_status === 'compliant').length / integrations.length) * 100) || 0}%</span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                  <div 
                    className="h-2 bg-emerald-500 rounded-full" 
                    style={{ width: `${(integrations.filter(i => i.compliance_status === 'compliant').length / integrations.length) * 100 || 0}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Non-Compliant Apps */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              {language === 'sr' ? 'Zahtijevaju pažnju' : 'Require Attention'}
            </h3>
            <div className="space-y-3">
              {integrations.filter(i => i.compliance_status !== 'compliant').slice(0, 5).map(app => (
                <div key={app.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center">
                      <Layers className="w-5 h-5 text-cyan-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{app.app_name}</div>
                      <div className="text-xs text-gray-500">{app.vendor_name}</div>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${complianceColors[app.compliance_status]}`}>
                    {app.compliance_status}
                  </span>
                </div>
              ))}
              {integrations.filter(i => i.compliance_status !== 'compliant').length === 0 && (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p>{language === 'sr' ? 'Sve aplikacije su usklađene' : 'All apps are compliant'}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Apps Tab */}
      {activeTab === 'apps' && (
        <div className="space-y-4">
          {/* Search & Filter */}
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === 'sr' ? 'Pretraži aplikacije...' : 'Search applications...'}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              {categories.map(cat => (
                <option key={cat.key} value={cat.key}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Apps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredIntegrations.map(app => (
              <div key={app.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg flex items-center justify-center">
                      <Layers className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">{app.app_name}</h4>
                      <span className="text-xs text-gray-500">{app.vendor_name}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`px-2 py-0.5 rounded text-xs ${complianceColors[app.compliance_status]}`}>
                    {app.compliance_status}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs ${dataClassificationColors[app.data_classification]}`}>
                    {app.data_classification}
                  </span>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  <span className="flex items-center gap-1" title="SSO">
                    {app.sso_enabled ? <Lock className="w-4 h-4 text-green-500" /> : <Unlock className="w-4 h-4 text-red-500" />}
                    SSO
                  </span>
                  <span className="flex items-center gap-1" title="MFA">
                    {app.mfa_enforced ? <Shield className="w-4 h-4 text-green-500" /> : <Shield className="w-4 h-4 text-red-500" />}
                    MFA
                  </span>
                  {app.user_count && (
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {app.user_count}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <button 
                    onClick={() => runComplianceCheck(app)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-cyan-700 bg-cyan-100 dark:bg-cyan-900/30 dark:text-cyan-300 rounded-lg hover:bg-cyan-200"
                  >
                    <Play className="w-4 h-4" />
                    {language === 'sr' ? 'Provjeri' : 'Check'}
                  </button>
                  <button 
                    onClick={() => openLinkModal(app)}
                    className="p-2 text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 rounded-lg"
                    title={language === 'sr' ? 'Poveži sa kontrolama' : 'Link to controls'}
                  >
                    <LinkIcon className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={() => handleSync(app.id)}
                    className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                    <Settings className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {language === 'sr' ? 'Kako funkcioniše SaaS provjera usklađenosti?' : 'How SaaS Compliance Checking Works'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="w-5 h-5 text-indigo-500" />
                  <span className="font-medium text-gray-900 dark:text-white">SSO Integration</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'sr' 
                    ? 'Provjerava da li je Single Sign-On omogućen i pravilno konfiguriran'
                    : 'Checks if Single Sign-On is enabled and properly configured'
                  }
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-green-500" />
                  <span className="font-medium text-gray-900 dark:text-white">MFA Enforcement</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'sr' 
                    ? 'Provjerava da li je Multi-Factor Authentication obavezna za sve korisnike'
                    : 'Verifies Multi-Factor Authentication is required for all users'
                  }
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="w-5 h-5 text-orange-500" />
                  <span className="font-medium text-gray-900 dark:text-white">Activity Logs</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'sr' 
                    ? 'Provjerava dostupnost i konfiguraciju audit logova'
                    : 'Checks availability and configuration of audit logs'
                  }
                </p>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-5 h-5 text-blue-500" />
                  <span className="font-medium text-gray-900 dark:text-white">Data Encryption</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'sr' 
                    ? 'Provjerava enkripciju podataka u mirovanju i u prijenosu'
                    : 'Verifies data encryption at rest and in transit'
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {language === 'sr' ? 'Status usklađenosti aplikacija' : 'Application Compliance Status'}
            </h3>
            <div className="space-y-3">
              {integrations.map(app => (
                <div key={app.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Layers className="w-5 h-5 text-cyan-500" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{app.app_name}</div>
                      <div className="text-xs text-gray-500">{app.vendor_name}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <span className={app.sso_enabled ? 'text-green-500' : 'text-red-500'}>
                        SSO {app.sso_enabled ? '✓' : '✗'}
                      </span>
                      <span className={app.mfa_enforced ? 'text-green-500' : 'text-red-500'}>
                        MFA {app.mfa_enforced ? '✓' : '✗'}
                      </span>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${complianceColors[app.compliance_status]}`}>
                      {app.compliance_status}
                    </span>
                    <button 
                      onClick={() => runComplianceCheck(app)}
                      className="px-3 py-1 text-sm bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 rounded hover:bg-cyan-200"
                    >
                      {language === 'sr' ? 'Provjeri' : 'Check'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Add Application Modal */}
      <Modal
        isOpen={showAddAppModal}
        onClose={() => setShowAddAppModal(false)}
        title={language === 'sr' ? 'Dodaj SaaS aplikaciju' : 'Add SaaS Application'}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowAddAppModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {language === 'sr' ? 'Otkaži' : 'Cancel'}
            </button>
            <button
              onClick={handleAddApp}
              disabled={saving || !newApp.app_name || !newApp.vendor_name}
              className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
            >
              {saving ? (language === 'sr' ? 'Dodavanje...' : 'Adding...') : (language === 'sr' ? 'Dodaj' : 'Add')}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Naziv aplikacije *' : 'Application Name *'}
            </label>
            <input
              type="text"
              value={newApp.app_name}
              onChange={(e) => setNewApp({...newApp, app_name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Salesforce, Slack, Jira..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Naziv vendora *' : 'Vendor Name *'}
            </label>
            <input
              type="text"
              value={newApp.vendor_name}
              onChange={(e) => setNewApp({...newApp, vendor_name: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Salesforce Inc., Atlassian..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Kategorija' : 'Category'}
            </label>
            <select
              value={newApp.category}
              onChange={(e) => setNewApp({...newApp, category: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="productivity">{language === 'sr' ? 'Produktivnost' : 'Productivity'}</option>
              <option value="security">{language === 'sr' ? 'Sigurnost' : 'Security'}</option>
              <option value="crm">CRM</option>
              <option value="hr">HR</option>
              <option value="finance">{language === 'sr' ? 'Financije' : 'Finance'}</option>
              <option value="other">{language === 'sr' ? 'Ostalo' : 'Other'}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {language === 'sr' ? 'Klasifikacija podataka' : 'Data Classification'}
            </label>
            <select
              value={newApp.data_classification}
              onChange={(e) => setNewApp({...newApp, data_classification: e.target.value as SaaSIntegration['data_classification']})}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="public">{language === 'sr' ? 'Javno' : 'Public'}</option>
              <option value="internal">{language === 'sr' ? 'Interno' : 'Internal'}</option>
              <option value="confidential">{language === 'sr' ? 'Povjerljivo' : 'Confidential'}</option>
              <option value="restricted">{language === 'sr' ? 'Ograničeno' : 'Restricted'}</option>
            </select>
          </div>
        </div>
      </Modal>

      {/* Compliance Check Modal */}
      <Modal
        isOpen={showComplianceCheckModal}
        onClose={() => setShowComplianceCheckModal(false)}
        title={language === 'sr' ? 'Rezultati provjere usklađenosti' : 'Compliance Check Results'}
      >
        {checkingCompliance ? (
          <div className="flex flex-col items-center py-8">
            <RefreshCw className="w-8 h-8 animate-spin text-cyan-500 mb-3" />
            <p className="text-gray-600 dark:text-gray-400">
              {language === 'sr' ? 'Provjeravam usklađenost...' : 'Checking compliance...'}
            </p>
          </div>
        ) : complianceResults ? (
          <div className="space-y-4">
            {selectedApp && (
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="font-medium text-gray-900 dark:text-white">{selectedApp.app_name}</div>
                <div className="text-sm text-gray-500">{selectedApp.vendor_name}</div>
              </div>
            )}
            {complianceResults.error ? (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300">
                {language === 'sr' 
                  ? 'Greška pri provjeri. Provjerite konfiguraciju integracije.'
                  : 'Error during check. Please verify integration configuration.'
                }
              </div>
            ) : (
              <div className="space-y-3">
                {complianceResults.checks?.map((check: any, idx: number) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <span className="text-gray-900 dark:text-white">{check.name}</span>
                    <span className={check.passed ? 'text-green-500' : 'text-red-500'}>
                      {check.passed ? '✓ Passed' : '✗ Failed'}
                    </span>
                  </div>
                )) || (
                  <p className="text-gray-500 dark:text-gray-400 text-center">
                    {language === 'sr' ? 'Nema detaljnih rezultata' : 'No detailed results available'}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : null}
      </Modal>

      {/* Link to Controls Modal */}
      <Modal
        isOpen={showLinkControlsModal}
        onClose={() => setShowLinkControlsModal(false)}
        title={language === 'sr' ? 'Poveži sa kontrolama' : 'Link to Controls'}
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowLinkControlsModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {language === 'sr' ? 'Otkaži' : 'Cancel'}
            </button>
            <button
              onClick={handleLinkToControls}
              disabled={saving || selectedControls.length === 0}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? (language === 'sr' ? 'Čuvanje...' : 'Saving...') : (language === 'sr' ? 'Poveži' : 'Link')}
            </button>
          </div>
        }
      >
        {selectedApp && (
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center gap-3">
                <Layers className="w-5 h-5 text-cyan-500" />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">{selectedApp.app_name}</div>
                  <div className="text-sm text-gray-500">
                    SSO: {selectedApp.sso_enabled ? '✓' : '✗'} | MFA: {selectedApp.mfa_enforced ? '✓' : '✗'}
                  </div>
                </div>
                <span className={`ml-auto px-2 py-1 rounded text-xs ${complianceColors[selectedApp.compliance_status]}`}>
                  {selectedApp.compliance_status}
                </span>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {language === 'sr' 
                ? 'Status usklađenosti ove aplikacije će biti dodat kao dokaz za odabrane kontrole'
                : 'This application compliance status will be added as evidence to selected controls'
              }
            </p>
            <ControlSelector
              frameworks={frameworks}
              selectedFramework={selectedFramework}
              onSelectFramework={setSelectedFramework}
              selectedControls={selectedControls}
              onSelectControls={setSelectedControls}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EnterpriseSaaS;
